'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Cards from './Cards';
import RichText from '@/components/ui/RichText/RichText';
import { FibroScanPackagesProps } from './types';

export default function FibroScanPackages({
  data,
  onEnquire = () => {},
}: FibroScanPackagesProps): React.ReactElement {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const [pageWidth, setPageWidth] = useState(0);

  const packages = useMemo(() => data?.packages ?? [], [data?.packages]);

  useEffect((): (() => void) => {
    const handle = (): void => {
      if (!trackRef.current) return;
      const w = trackRef.current.clientWidth || 0;
      setPageWidth(w);
      trackRef.current.scrollTo({ left: w * active, behavior: 'auto' });
    };

    handle();
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, [active]);

  const onScroll = (): void => {
    if (!trackRef.current || !pageWidth) return;
    const idx = Math.round(trackRef.current.scrollLeft / pageWidth);
    if (idx !== active) setActive(idx);
  };

  const goTo = (idx: number): void => {
    if (!trackRef.current || !pageWidth) return;
    trackRef.current.scrollTo({ left: idx * pageWidth, behavior: 'smooth' });
  };

  return (
    <section
      id="Packages"
      className="content-wrapper font-regular mx-auto scroll-m-36 px-4"
    >
      <div className="flex flex-col items-center justify-center text-center">
        {data?.heading?.content && (
          <h2>
            <RichText styles={data.heading.styles}>
              {data.heading.content}
            </RichText>
          </h2>
        )}
        {data?.description?.content && (
          <RichText styles={data.description.styles}>
            {data.description.content}
          </RichText>
        )}
      </div>

      {packages.length > 0 && (
        <div className="mt-4 md:mt-6 md:hidden">
          <div
            ref={trackRef}
            onScroll={onScroll}
            className="relative -mx-4 snap-x snap-mandatory overflow-x-auto px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex gap-4">
              {packages.map((pkg) => (
                <div key={pkg.id} className="w-full shrink-0 snap-start pb-2">
                  <Cards
                    title={pkg.title}
                    testType={pkg.testType}
                    price={pkg.price}
                    description={pkg.description}
                    subDescription={pkg.subDescription}
                    notes={pkg.notes}
                    hasButton={pkg.hasButton}
                    onEnquire={() =>
                      onEnquire({
                        category: 'FibroScan',
                        title: pkg.title,
                        price: pkg.price,
                      })
                    }
                    defaultOpen={false}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-center gap-2">
            {packages.map((_, i) => (
              <button
                key={`dot-${i}`}
                onClick={() => goTo(i)}
                aria-label={`Go to card ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  active === i
                    ? 'bg-surface-info-indicator w-6'
                    : 'bg-surface-indicator-muted w-2'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {packages.length > 0 && (
        <div className="mt-6 hidden grid-cols-2 items-stretch gap-6 md:grid lg:grid-cols-3">
          {packages.map((pkg) => (
            <div key={pkg.id} className="break-inside-avoid">
              <Cards
                title={pkg.title}
                testType={pkg.testType}
                price={pkg.price}
                description={pkg.description}
                subDescription={pkg.subDescription}
                notes={pkg.notes}
                hasButton={pkg.hasButton}
                onEnquire={() =>
                  onEnquire({
                    category: 'FibroScan',
                    title: pkg.title,
                    price: pkg.price,
                  })
                }
                defaultOpen={false}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
